/* ============================================================================
   Nex Companion Questionnaire (Chunk B2)
   ----------------------------------------------------------------------------
   A short, opinionated 7-question flow that captures the user's priorities
   map. The result is the single source of truth that powers Custom Dexes,
   Storage Optimiser defaults, feature elevation in Profile, and (Chunk C)
   friend matching.

   Design notes:
     - Modal sits over the app, dismissible only via Save or Skip. Skip lets
       the user out without writing answers, the app keeps running off the
       balanced defaults.
     - Single source of truth, do not split this UI per surface. Whenever we
       want to tune behaviour, we add a question here and a derivation in
       lib/userPriorities.ts, never a separate setting.
     - No NSFW content, no "tell us your name" busywork. Every question maps
       to a behaviour the app changes.
============================================================================ */

import { useEffect, useState } from "react";
import { useSession } from "./SessionProvider";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import {
  ChevronLeft, ChevronRight, X, Check,
  Swords, Mountain, BookOpenCheck, ArrowLeftRight, Coffee,
  Sparkles, Stars, Clover, Hash, CloudLightning, Minimize2, Maximize2, Sparkle,
  Trophy, Zap, Crown, Target,
  Battery, BatteryLow, BatteryFull,
  Package, PackageCheck, PackageOpen,
} from "lucide-react";
import {
  type PlayStyle, type CollectionFocus, type LeagueFocus,
  type DustBudget, type BagPressure, type TradeFrequency,
  type UserPriorities,
  DEFAULT_USER_PRIORITIES,
  playStyleLabel, collectionFocusLabel, leagueFocusLabel,
} from "@/lib/userPriorities";

// ── Question metadata ─────────────────────────────────────────────────────

interface QuestionMeta {
  key:
    | "primaryStyle"
    | "secondaryStyle"
    | "collectionFoci"
    | "leagueFoci"
    | "dustBudget"
    | "bagPressure"
    | "tradeFrequency";
  title: string;
  subtitle: string;
  multi: boolean;
  // Optional cap on multi-select. Undefined = unlimited within options list.
  maxSelections?: number;
}

const QUESTIONS: QuestionMeta[] = [
  {
    key: "primaryStyle",
    title: "What brings you to GO?",
    subtitle: "Pick the one that's most you. We'll tune the whole app around this.",
    multi: false,
  },
  {
    key: "secondaryStyle",
    title: "And on the side?",
    subtitle: "Optional. Pick a secondary lean, or skip if your primary covers it.",
    multi: false,
  },
  {
    key: "collectionFoci",
    title: "Which trophies are you chasing?",
    subtitle: "Multi-select, in priority order. We'll auto-build a custom dex per pick.",
    multi: true,
    maxSelections: 4,
  },
  {
    key: "leagueFoci",
    title: "Which leagues do you actually play?",
    subtitle: "Pick all that apply. Skip if PvP isn't your thing.",
    multi: true,
    maxSelections: 4,
  },
  {
    key: "dustBudget",
    title: "How's your stardust?",
    subtitle: "Drives how aggressive the optimiser is with luckies and PvP picks.",
    multi: false,
  },
  {
    key: "bagPressure",
    title: "How full is your bag, usually?",
    subtitle: "If you're always hitting cap, the optimiser leans tighter on defaults.",
    multi: false,
  },
  {
    key: "tradeFrequency",
    title: "How often do you trade?",
    subtitle: "Daily traders get extra rank-floor protection on PvP picks.",
    multi: false,
  },
];

// ── Option definitions ────────────────────────────────────────────────────

interface Option<T extends string> {
  value: T;
  label: string;
  hint?: string;
  icon: React.ReactNode;
}

const PLAY_STYLES: Option<PlayStyle>[] = [
  { value: "pvp",       label: playStyleLabel("pvp"),       hint: "Battle leagues, team building, ranked",   icon: <Swords className="w-5 h-5" /> },
  { value: "raids",     label: playStyleLabel("raids"),     hint: "Tier 5s, Mega/Shadow raids, candy XL",     icon: <Mountain className="w-5 h-5" /> },
  { value: "collector", label: playStyleLabel("collector"), hint: "Living dex, shinies, perfects, costumes",  icon: <BookOpenCheck className="w-5 h-5" /> },
  { value: "trader",    label: playStyleLabel("trader"),    hint: "Lucky farming, regional swaps, gap-fills", icon: <ArrowLeftRight className="w-5 h-5" /> },
  { value: "casual",    label: playStyleLabel("casual"),    hint: "I just play when I play",                  icon: <Coffee className="w-5 h-5" /> },
];

// Order, left to right, top to bottom: Shiny Dex, Shundos, Hundos, Dynamax,
// Lucky, XXS, XXL, Shiny Lucky (2026-08-25 ask — the four retired tiles
// were Male/Female, Variants, Costumes, PvP Top 10). Every tile now maps
// 1:1 onto a dex the app tracks, and picking one links that dex to the
// Profile page's trophy panel (focusToDexVariant).
const COLLECTION_FOCI: Option<CollectionFocus>[] = [
  { value: "shiny_dex",   label: collectionFocusLabel("shiny_dex"),   hint: "Every shiny, all forms",       icon: <Sparkles className="w-4 h-4" /> },
  { value: "shundo",      label: collectionFocusLabel("shundo"),      hint: "Shiny + 100% IV",              icon: <Stars className="w-4 h-4" /> },
  { value: "hundos",      label: "Hundos (4★ Perfect)",                hint: "100% IV, any form",            icon: <Hash className="w-4 h-4" /> },
  { value: "dynamax",     label: collectionFocusLabel("dynamax"),     hint: "Max Battle catches",           icon: <CloudLightning className="w-4 h-4" /> },
  { value: "lucky",       label: collectionFocusLabel("lucky"),       hint: "Half stardust to power up",    icon: <Clover className="w-4 h-4" /> },
  { value: "xxs",         label: collectionFocusLabel("xxs"),         hint: "Record-small sizes",           icon: <Minimize2 className="w-4 h-4" /> },
  { value: "xxl",         label: collectionFocusLabel("xxl"),         hint: "Record-large sizes",           icon: <Maximize2 className="w-4 h-4" /> },
  { value: "shiny_lucky", label: collectionFocusLabel("shiny_lucky"), hint: "Shiny + lucky, trade unicorn", icon: <Sparkle className="w-4 h-4" /> },
];

const LEAGUE_FOCI: Option<LeagueFocus>[] = [
  { value: "great",        label: leagueFocusLabel("great"),        hint: "1500 CP cap",       icon: <Trophy className="w-4 h-4" /> },
  { value: "ultra",        label: leagueFocusLabel("ultra"),        hint: "2500 CP cap",       icon: <Zap className="w-4 h-4" /> },
  { value: "master",       label: leagueFocusLabel("master"),       hint: "No cap",            icon: <Crown className="w-4 h-4" /> },
  { value: "limited_meta", label: leagueFocusLabel("limited_meta"), hint: "Cups, theme weeks", icon: <Target className="w-4 h-4" /> },
  { value: "none",         label: leagueFocusLabel("none"),         hint: "Skip PvP entirely", icon: <X className="w-4 h-4" /> },
];

const DUST_BUDGETS: Option<DustBudget>[] = [
  { value: "tight",     label: "Tight, every dust counts",  icon: <BatteryLow className="w-5 h-5" /> },
  { value: "moderate",  label: "Moderate, careful spender", icon: <Battery className="w-5 h-5" /> },
  { value: "abundant",  label: "Abundant, dust to burn",    icon: <BatteryFull className="w-5 h-5" /> },
];

const BAG_PRESSURES: Option<BagPressure>[] = [
  { value: "always_full", label: "Always at the cap",     icon: <Package className="w-5 h-5" /> },
  { value: "sometimes",   label: "Sometimes tight",       icon: <PackageCheck className="w-5 h-5" /> },
  { value: "rarely",      label: "Rarely hit the limit",  icon: <PackageOpen className="w-5 h-5" /> },
];

const TRADE_FREQUENCIES: Option<TradeFrequency>[] = [
  { value: "daily",   label: "Daily, lucky-farming routine", icon: <ArrowLeftRight className="w-5 h-5" /> },
  { value: "weekly",  label: "A few times a week",           icon: <ArrowLeftRight className="w-5 h-5" /> },
  { value: "rarely",  label: "Rarely or never",              icon: <X className="w-5 h-5" /> },
];

function optionsFor(key: QuestionMeta["key"]): Option<string>[] {
  switch (key) {
    case "primaryStyle":
    case "secondaryStyle": return PLAY_STYLES as Option<string>[];
    case "collectionFoci": return COLLECTION_FOCI as Option<string>[];
    case "leagueFoci":     return LEAGUE_FOCI as Option<string>[];
    case "dustBudget":     return DUST_BUDGETS as Option<string>[];
    case "bagPressure":    return BAG_PRESSURES as Option<string>[];
    case "tradeFrequency": return TRADE_FREQUENCIES as Option<string>[];
  }
}

// ── Local draft type ──────────────────────────────────────────────────────

type DraftPriorities = Omit<UserPriorities, "questionnaireCompletedAt" | "questionnaireVersion">;

// ── Component ─────────────────────────────────────────────────────────────

export function NexQuestionnaire() {
  const {
    nexQuestionnaireOpen, closeNexQuestionnaire,
    completeNexQuestionnaire, userPriorities,
  } = useSession();
  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState<DraftPriorities>(seedFromExisting(userPriorities));

  // Reset draft + index whenever the modal is opened so re-edits start from
  // the latest saved priorities (or defaults if first run).
  useEffect(() => {
    if (nexQuestionnaireOpen) {
      setDraft(seedFromExisting(userPriorities));
      setIdx(0);
    }
  }, [nexQuestionnaireOpen, userPriorities]);

  if (!nexQuestionnaireOpen) return null;

  const q = QUESTIONS[idx];
  const isLast = idx === QUESTIONS.length - 1;
  const total = QUESTIONS.length;

  function next() {
    if (isLast) {
      completeNexQuestionnaire(draft);
    } else {
      setIdx((i) => Math.min(i + 1, total - 1));
    }
  }
  function prev() { setIdx((i) => Math.max(i - 1, 0)); }
  function skip() {
    // Skip means "use defaults for now, prompt me later". We close without
    // marking the questionnaire as completed so re-prompts can re-open it.
    closeNexQuestionnaire();
  }

  return (
    <div
      role="dialog"
      aria-label="Nexus Dex priorities questionnaire"
      aria-modal="true"
      className="fixed inset-0 z-[96] flex flex-col anim-fade-up overflow-hidden"
      style={{ background: "var(--surface-overlay)" }}
      data-testid="dialog-nex-questionnaire"
    >

      {/* Top bar — b13d T5: respect the iOS safe-area top so the logo / Skip
          never sit under the notch / Dynamic Island on the full-screen sheet.
          b15b U5: give the bar its own opaque backing + a hairline divider and
          bump it above the scroll layer (z-10) so the option tiles slide
          cleanly UNDER it on scroll instead of bleeding through the chrome.
          b19-ui-dex-completion: adopt the global centred Nexus Dex header. A
          three-column grid keeps the brand mark exactly centred (mirroring the
          AppShell sticky header) while the Skip control sits in the right slot,
          replacing the old left-aligned logo + "NEX COMPANION" label. */}
      <div
        className="relative z-10 grid grid-cols-[1fr_auto_1fr] items-center px-5 pt-5 pb-3 page-safe-top border-b border-[var(--border-subtle)]"
        style={{ background: "var(--surface-overlay)" }}
      >
        <span aria-hidden />
        <div className="flex items-center justify-center">
          <Logo size={30} withWordmark variant="mark" />
        </div>
        <div className="flex items-center justify-end">
          <button
            onClick={skip}
            data-testid="button-questionnaire-skip"
            className="text-xs font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] inline-flex items-center gap-1 px-3 py-1.5 rounded-[var(--radius-pill)] hover:bg-[var(--accent-subtle)] transition-colors"
            aria-label="Skip for now"
          >
            Skip <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Body — b15b U5: pt-7 pushes the first question row clear of the top
          bar so it never opens jammed against the chrome. */}
      <div className="relative flex-1 overflow-y-auto px-5 pb-5 pt-7">
        <div className="mx-auto max-w-md w-full">
          <div className="text-center mb-5">
            <p className="text-[10px] text-[var(--accent-fill)]">
              Question {idx + 1} of {total}
            </p>
            <h2 className="text-xl font-bold tracking-tight mt-1 text-[var(--text-primary)]" style={{ color: "var(--text-primary)" }}>
              {q.title}
            </h2>
            <p className="text-[13px] font-semibold mt-1.5 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {q.subtitle}
            </p>
            {q.multi && q.maxSelections ? (
              <p className="text-[11px] font-medium text-[var(--text-tertiary)] mt-2">
                Pick up to {q.maxSelections}, in priority order.
              </p>
            ) : null}
          </div>

          <QuestionBody q={q} draft={draft} setDraft={setDraft} />
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 pb-7 pt-3 border-t border-border/30">
        <div className="mx-auto max-w-md w-full">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            {QUESTIONS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Go to question ${i + 1}`}
                data-testid={`questionnaire-dot-${i}`}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === idx ? 24 : 8,
                  background: i === idx ? "hsl(142 100% 56%)" : "hsl(142 30% 30%)",
                  boxShadow: i === idx ? "0 0 8px hsl(142 100% 50% / 0.6)" : undefined,
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {idx > 0 ? (
              <button
                onClick={prev}
                data-testid="button-questionnaire-prev"
                className="h-12 px-4 rounded-full text-sm font-semibold inline-flex items-center gap-1"
                style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-default)", color: "var(--text-primary)" }}
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            ) : <span className="w-1" />}
            <button
              onClick={next}
              data-testid={isLast ? "button-questionnaire-save" : "button-questionnaire-next"}
              className="flex-1 h-12 rounded-[var(--radius-pill)] inline-flex items-center justify-center disabled:opacity-50 bg-[var(--accent-fill)] text-[var(--text-on-accent)] font-semibold hover:bg-[var(--accent-hover)] transition-colors"
              disabled={!isStepValid(q, draft)}
            >
              {isLast ? (
                <>Save priorities <Check className="w-4 h-4 ml-1" /></>
              ) : (
                <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Question body, renders the option grid for the current question ──────

function QuestionBody({
  q, draft, setDraft,
}: {
  q: QuestionMeta;
  draft: DraftPriorities;
  setDraft: React.Dispatch<React.SetStateAction<DraftPriorities>>;
}) {
  const opts = optionsFor(q.key);

  // Multi-select questions, with priority ordering preserved.
  if (q.multi) {
    const current = (draft[q.key] as string[]) ?? [];
    const toggle = (v: string) => {
      setDraft((prev) => {
        const cur = (prev[q.key] as string[]) ?? [];
        if (cur.includes(v)) {
          return { ...prev, [q.key]: cur.filter((x) => x !== v) };
        }
        if (q.maxSelections && cur.length >= q.maxSelections) return prev;
        // Special case for leagueFoci: picking "none" clears the rest.
        if (q.key === "leagueFoci" && v === "none") {
          return { ...prev, leagueFoci: ["none"] };
        }
        // Picking anything else when "none" is set, drops "none".
        if (q.key === "leagueFoci" && cur.includes("none")) {
          return { ...prev, leagueFoci: [v as LeagueFocus] };
        }
        return { ...prev, [q.key]: [...cur, v] };
      });
    };
    return (
      <>
        <div className="grid grid-cols-2 gap-2.5">
          {opts.map((o) => {
            const selected = current.includes(o.value);
            const order = current.indexOf(o.value);
            return (
              <button
                key={o.value}
                onClick={() => toggle(o.value)}
                data-testid={`option-${q.key}-${o.value}`}
                className="text-left rounded-2xl p-3 transition-all active:scale-[0.98]"
                style={{
                  background: selected ? "hsl(142 80% 18% / 0.7)" : "var(--surface-dark-7-85)",
                  border: selected ? "1.5px solid hsl(142 100% 56%)" : "1px solid hsl(142 80% 35% / 0.45)",
                  boxShadow: selected ? "0 0 16px hsl(142 100% 50% / 0.35)" : undefined,
                  backdropFilter: "blur(8px)",
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: selected ? "var(--accent-fill)" : "var(--accent-subtle)", color: selected ? "var(--text-on-accent)" : "var(--accent-fill)", border: "1px solid transparent" }}
                  >
                    {o.icon}
                  </span>
                  {selected ? (
                    <span
                      className="w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center"
                      style={{
                        background: "var(--accent-fill)",
                        color: "var(--surface-base)",
                        boxShadow: "0 0 8px hsl(142 100% 50% / 0.6)",
                      }}
                    >
                      {order + 1}
                    </span>
                  ) : null}
                </div>
                <p className="text-[13px] font-extrabold mt-2 leading-tight" style={{ color: selected ? "var(--text-on-accent)" : "var(--text-primary)" }}>{o.label}</p>
                {o.hint ? <p className="text-[11px] font-semibold mt-0.5 leading-snug" style={{ color: selected ? "hsl(142 100% 92%)" : "var(--text-secondary)" }}>{o.hint}</p> : null}
              </button>
            );
          })}
        </div>
      </>
    );
  }

  // Single-select question. secondaryStyle uniquely allows clearing.
  const current = draft[q.key] as string | null;

  const pick = (v: string) => {
    setDraft((prev) => {
      // secondaryStyle: tapping the already-selected option clears it.
      if (q.key === "secondaryStyle" && prev.secondaryStyle === v) {
        return { ...prev, secondaryStyle: null };
      }
      return { ...prev, [q.key]: v };
    });
  };

  return (
    <div className="space-y-2.5">
      {q.key === "secondaryStyle" ? (
        <p className="text-[11px] font-semibold text-center -mt-2 mb-1" style={{ color: "var(--text-secondary)" }}>
          Tap your selection again to clear it.
        </p>
      ) : null}
      {opts.map((o) => {
        const selected = current === o.value;
        return (
          <button
            key={o.value}
            onClick={() => pick(o.value)}
            data-testid={`option-${q.key}-${o.value}`}
            className="w-full text-left rounded-2xl p-3.5 transition-all active:scale-[0.99]"
            style={{
              background: selected ? "hsl(174 70% 30% / 0.55)" : "var(--surface-dark-7-85)",
              border: selected ? "1px solid hsl(174 70% 60%)" : "1px solid hsl(142 80% 35% / 0.45)",
              backdropFilter: "blur(8px)",
            }}
          >
            <div className="flex items-center gap-3">
              <span
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: selected ? "hsl(174 70% 50% / 0.5)" : "var(--accent-subtle)",
                  color: selected ? "white" : "var(--accent-fill)",
                  border: selected ? "1px solid hsl(174 70% 70%)" : "1px solid hsl(var(--primary) / 0.45)",
                }}
              >
                {o.icon}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-extrabold leading-tight" style={{ color: selected ? "var(--text-on-accent)" : "var(--text-primary)" }}>{o.label}</p>
                {o.hint ? <p className="text-[12px] font-semibold mt-0.5 leading-snug" style={{ color: selected ? "hsl(174 100% 92%)" : "var(--text-secondary)" }}>{o.hint}</p> : null}
              </div>
              {selected ? (
                <Check className="w-5 h-5 text-white shrink-0" />
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── Validation per step ───────────────────────────────────────────────────

function isStepValid(q: QuestionMeta, draft: DraftPriorities): boolean {
  switch (q.key) {
    case "primaryStyle":   return Boolean(draft.primaryStyle);
    case "secondaryStyle": return true; // optional
    case "collectionFoci": return draft.collectionFoci.length > 0;
    case "leagueFoci":     return draft.leagueFoci.length > 0;
    case "dustBudget":     return Boolean(draft.dustBudget);
    case "bagPressure":    return Boolean(draft.bagPressure);
    case "tradeFrequency": return Boolean(draft.tradeFrequency);
  }
}

// ── Seed helper ───────────────────────────────────────────────────────────

// Migrate saved collection foci to the v3 tile set. v3 (2026-08-25) retired
// the gender_pair / variants / costumes / pvp_top tiles and added dynamax /
// xxs / xxl / shiny_lucky, so:
//   - the eight current tiles pass through (shiny_lucky is a REAL tile
//     again — the v2 migration used to collapse it into lucky);
//   - retired tile values drop out of the draft (their saved blobs keep
//     working app-wide, but a re-edit can't show a selection with no tile);
//   - v1 strays: shiny_male / shiny_female fold into shiny_dex, regional
//     and legacy_moves drop silently.
function migrateCollectionFoci(raw: readonly string[]): CollectionFocus[] {
  const out: CollectionFocus[] = [];
  const push = (f: CollectionFocus) => {
    if (!out.includes(f)) out.push(f);
  };
  for (const v of raw) {
    if (v === "shiny_dex" || v === "shundo" || v === "hundos" || v === "lucky"
      || v === "dynamax" || v === "xxs" || v === "xxl" || v === "shiny_lucky") {
      push(v as CollectionFocus);
      continue;
    }
    if (v === "shiny_male" || v === "shiny_female") { push("shiny_dex"); continue; }
    // gender_pair / variants / costumes / pvp_top (retired tiles), regional,
    // legacy_moves and any other unknown values are dropped silently.
  }
  return out;
}

function seedFromExisting(p: UserPriorities): DraftPriorities {
  // If the user has never completed the questionnaire we use the balanced
  // defaults but blank out the primary style so they're forced to pick. This
  // prevents an accidental "Save" on first run from falsely committing the
  // collector default.
  const seed: DraftPriorities = {
    primaryStyle: p.primaryStyle,
    secondaryStyle: p.secondaryStyle,
    collectionFoci: migrateCollectionFoci(p.collectionFoci),
    genderPairMode: p.genderPairMode ?? "shiny",
    leagueFoci: [...p.leagueFoci],
    dustBudget: p.dustBudget,
    bagPressure: p.bagPressure,
    tradeFrequency: p.tradeFrequency,
  };
  if (p.questionnaireCompletedAt === null) {
    return {
      ...DEFAULT_USER_PRIORITIES,
      // Preserve any selections the user partially filled in if we ever
      // wire that, but for now first-run starts from defaults.
      primaryStyle: seed.primaryStyle,
    };
  }
  return seed;
}
